/* global firebase */
import config from '#/config/firebase';
import { screenViewAnalyticsEvent } from '#/config/constants';
import {
  canSendAnalyticsEvent,
  createOperationalInstanceId,
  getAnalyticsEventParams,
} from '#/services/analytics/policy';
import {
  bufferAnalyticsEvent,
  drainAnalyticsEvents,
  getFirebaseInstallationId,
  logFirebaseAnalyticsEvent,
} from '#/services/firestore/analyticsBootstrap';
import {
  hasAnalyticsConsent,
  isConsentReady,
  onConsentChange,
} from '#/services/consent/consent';

const isClient = typeof window !== 'undefined';

const defaultAnalyticsParams = {
  device_type: 'web',
  device_model: isClient ? window.navigator.userAgent : '',
};

const operationalInstanceId = createOperationalInstanceId(
  isClient ? window.crypto : undefined,
);
let operationalEventSequence = 0;

const getOperationalAnalyticsParams = () => {
  operationalEventSequence += 1;

  return {
    ...defaultAnalyticsParams,
    operational_instance_id: operationalInstanceId,
    operational_unique_key: `${operationalInstanceId}:${operationalEventSequence}`,
  };
};

let defaultUserProperties = {};
let currentScreenName;
let currentUserId;

const api = {
  // Add default properties so user properties are guaranteed to be reset before analytics calls
  updateDefaultUserProperties(properties) {
    defaultUserProperties = {
      ...defaultUserProperties,
      ...properties,
    };
  },
};
const bufferedScreenEvents = [];
const bufferedEvents = [];
let firebaseAnalyticsLoaded = false;
let firebaseAnalyticsLoading = false;
let firebaseReady = false;

api.setUserProperties = properties => {
  api.updateDefaultUserProperties(properties);
};
api.setUserId = userId => {
  currentUserId = userId;
};
api.setCurrentScreen = screenName => {
  currentScreenName = screenName;
};

const initializeFirebaseAnalytics = () => {
  let analytics;

  try {
    analytics = firebase.analytics();
  } catch (error) {
    firebaseAnalyticsLoading = false;
    console.warn(
      '[Firebase] Analytics initialization failed; a later heartbeat will retry.',
      error,
    );
    return;
  }

  firebaseAnalyticsLoading = false;
  firebaseAnalyticsLoaded = true;

  api.sendAnalytics = (evt, payload = {}) => {
    const analyticsConsent = hasAnalyticsConsent();
    const eventParams = getAnalyticsEventParams(
      evt,
      payload,
      analyticsConsent
        ? defaultAnalyticsParams
        : getOperationalAnalyticsParams(),
      analyticsConsent,
    );

    return eventParams
      ? logFirebaseAnalyticsEvent(analytics, evt, eventParams)
      : undefined;
  };

  api.setUserProperties = properties => {
    api.updateDefaultUserProperties(properties);
    if (hasAnalyticsConsent()) {
      analytics.setUserProperties(defaultUserProperties);
    }
  };
  api.setUserId = userId => {
    currentUserId = userId;
    if (hasAnalyticsConsent()) {
      analytics.setUserId(userId);
    }
  };
  api.setCurrentScreen = screenName => {
    currentScreenName = screenName;
    if (hasAnalyticsConsent()) {
      analytics.setCurrentScreen(screenName);
    }
  };

  const updateAnalyticsContext = () => {
    if (hasAnalyticsConsent()) {
      if (currentUserId) {
        analytics.setUserId(currentUserId);
      }

      if (currentScreenName) {
        analytics.setCurrentScreen(currentScreenName);
      }

      analytics.setUserProperties(defaultUserProperties);
    } else {
      analytics.setUserId(null);
      analytics.setCurrentScreen(null);
      analytics.setUserProperties(
        Object.keys(defaultUserProperties).reduce(
          (properties, key) => ({ ...properties, [key]: null }),
          {},
        ),
      );
    }
  };

  updateAnalyticsContext();
  onConsentChange(updateAnalyticsContext);

  api.sendScreenViewAnalytics = screenName => {
    api.setCurrentScreen(screenName);
    api.setUserProperties(defaultUserProperties);
    api.sendAnalytics(screenViewAnalyticsEvent, {
      screen_name: screenName,
    });
  };

  drainAnalyticsEvents(bufferedEvents, event =>
    api.sendAnalytics(event.evt, event.payload),
  );
  drainAnalyticsEvents(bufferedScreenEvents, api.sendScreenViewAnalytics);
};

const loadFirebaseAnalytics = () => {
  if (
    !isClient ||
    !firebaseReady ||
    firebaseAnalyticsLoading ||
    firebaseAnalyticsLoaded ||
    !isConsentReady()
  ) {
    return;
  }

  if (typeof firebase.analytics === 'function') {
    initializeFirebaseAnalytics();
    return;
  }

  firebaseAnalyticsLoading = true;

  const firebaseAnalyticsScript = document.createElement('script');
  firebaseAnalyticsScript.src = config.FIREBASE_ANALYTICS_SCRIPT;
  firebaseAnalyticsScript.onload = initializeFirebaseAnalytics;
  firebaseAnalyticsScript.onerror = error => {
    firebaseAnalyticsLoading = false;
    firebaseAnalyticsScript.remove();
    console.warn(
      '[Firebase] Analytics script unavailable; a later heartbeat will retry.',
      error,
    );
  };

  document.head.appendChild(firebaseAnalyticsScript);
};

// If client browser, load firestore scripts, avoid during webpack compilation

if (isClient) {
  const firebaseAppScript = document.createElement('script');
  firebaseAppScript.src = config.FIREBASE_APP_SCRIPT;
  firebaseAppScript.onload = () => {
    // Init after script load
    firebase.initializeApp({
      apiKey: config.apiKey,
      authDomain: config.authDomain,
      projectId: config.projectId,
      storageBucket: config.storageBucket,
      messagingSenderId: config.messagingSenderId,
      appId: config.appId,
      // Keep a local fallback so Analytics can start when Firebase's remote
      // web-config request is unavailable.
      measurementId: config.measurementId,
    });
    firebaseReady = true;

    const firebaseInstallationScript = document.createElement('script');
    firebaseInstallationScript.src = config.FIREBASE_INSTALLATIONS_SCRIPT;

    const loadFirestoreAndAnalytics = () => {
      const firestoreScript = document.createElement('script');
      firestoreScript.src = config.FIREBASE_STORE_SCRIPT;

      document.head.appendChild(firestoreScript);
      loadFirebaseAnalytics();
    };

    firebaseInstallationScript.onload = () => {
      loadFirestoreAndAnalytics();

      getFirebaseInstallationId(firebase.installations()).then(
        installationId => {
          if (installationId) {
            defaultAnalyticsParams.instance_id = installationId;
          }
        },
      );
    };
    firebaseInstallationScript.onerror = error => {
      console.warn(
        '[Firebase] Installations script unavailable; continuing without it.',
        error,
      );
      loadFirestoreAndAnalytics();
    };

    document.head.appendChild(firebaseInstallationScript);
  };

  document.head.appendChild(firebaseAppScript);
}

api.sendAnalytics = (evt, payload = {}) => {
  if (!canSendAnalyticsEvent(evt, hasAnalyticsConsent())) {
    return;
  }

  bufferAnalyticsEvent(bufferedEvents, { evt, payload });
  loadFirebaseAnalytics();
};
api.sendScreenViewAnalytics = screenName => {
  if (hasAnalyticsConsent()) {
    bufferAnalyticsEvent(bufferedScreenEvents, screenName);
    loadFirebaseAnalytics();
  }
};

onConsentChange(loadFirebaseAnalytics);

api.getAllByCollection = async (collectionName, orderBy, age) => {
  const firestore = firebase?.firestore();
  const response = orderBy
    ? age
      ? await firestore
          .collection(collectionName)
          .where('ages', 'array-contains', age)
          .orderBy(orderBy, 'asc')
          .where('hide', '==', false)
          .get()
      : await firestore
          .collection(collectionName)
          .orderBy(orderBy, 'asc')
          .where('hide', '==', false)
          .get()
    : await firestore
        .collection(collectionName)
        .where('hide', '==', false)
        .get();

  const currentSeconds = Date.now() / 1000;
  return response.docs.reduce((result, doc) => {
    const data = doc.data();
    if (!data.start_date || !data.end_date) {
      result.push(data);
      return result;
    }

    if (
      data.start_date.seconds < currentSeconds &&
      data.end_date.seconds > currentSeconds
    ) {
      result.push(data);
    }
    return result;
  }, []);
};

api.getAvailableTopics = async age => {
  const firestore = firebase?.firestore();
  const query = firestore
    .collection('topics')
    .where('hide', '==', false)
    .where('ages', 'array-contains', age)
    .orderBy('name', 'asc');
  const response = await query.get();
  return response.docs.map(doc => doc.data());
};

api.getTopic = async id => {
  const firestore = firebase?.firestore();
  const query = firestore
    .collection('topics')
    .where('hide', '==', false)
    .where('id', '==', id);
  const response = await query.get();
  return response.docs.map(doc => doc.data());
};

api.getAvailableShows = async age => {
  const firestore = firebase?.firestore();
  const query = firestore
    .collection('shows')
    .where('hide', '==', false)
    .where('ages', 'array-contains', age)
    .orderBy('name', 'asc');
  const response = await query.get();
  const currentSeconds = Date.now() / 1000;
  return response.docs.reduce((result, doc) => {
    const data = doc.data();
    if (
      data.start_date.seconds < currentSeconds &&
      data.end_date.seconds > currentSeconds
    ) {
      result.push(data);
    }
    return result;
  }, []);
};

api.getContentById = async (contentId, collectionName) => {
  const firestore = firebase?.firestore();
  const response = await firestore
    .collection(collectionName)
    .where('id', '==', contentId)
    .get();
  return response.docs.map(doc => doc.data());
};

api.getAvatars = async () => {
  const firestore = firebase?.firestore();
  const response = await firestore.collection('avatars').get();
  return response.docs.map(doc => doc.data());
};

export default api;
